import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://education-crm-2b77a26760ff.herokuapp.com/api';
const NAVY = '#1B2A6B';
const GOLD = '#C9A84C';

const TIERS = [
  {
    name: 'Micro Operator',
    icon: '🌱',
    investment: 'Under ₹5L',
    commission: '₹8,000 / enrollment',
    coverage: 'Single College',
    features: ['1 dedicated college', 'Full training provided', 'Marketing support', 'CRM access'],
  },
  {
    name: 'Area Franchise',
    icon: '🏙️',
    investment: '₹5L – ₹10L',
    commission: '₹10,500 / enrollment',
    coverage: '1 City / District',
    features: ['Up to 10 colleges', 'Team training', 'Lead management', 'Revenue dashboard', 'Priority support'],
    highlight: true,
  },
  {
    name: 'Regional Franchise',
    icon: '🗺️',
    investment: '₹10L – ₹25L',
    commission: '₹12,600 / enrollment',
    coverage: '1 State / Region',
    features: ['Unlimited colleges', 'Sub-franchise rights', 'Dedicated RCM', 'Monthly reviews', 'White-label option'],
  },
  {
    name: 'Master Franchise',
    icon: '🏆',
    investment: '₹25L+',
    commission: 'Custom',
    coverage: 'Multi-State / National',
    features: ['National rights', 'Sub-franchise management', 'Board-level support', 'Co-branding', 'Equity discussion'],
  },
];

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana',
  'Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur',
  'Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh',
  'Chandigarh','Puducherry','Other',
];

const inputStyle = {
  width: '100%', padding: '12px 14px', fontSize: 15, border: '1.5px solid #e2e8f0',
  borderRadius: 10, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  background: '#fff', color: '#1e293b',
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' };

export default function FranchisePage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    full_name: '', city: '', state: '', phone: '', email: '',
    current_profession: '', investment_capacity: '', franchise_type_interest: '', brief_background: '',
  });

  // Capture UTM params from URL
  const urlParams = new URLSearchParams(window.location.search);
  const utmData = {
    utm_source:   urlParams.get('utm_source'),
    utm_medium:   urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term:     urlParams.get('utm_term'),
    utm_content:  urlParams.get('utm_content'),
  };

  useEffect(() => {
    // Track page view
    axios.get(`${BASE_URL}/funnel/franchise/track-view`, { params: utmData }).catch(() => {});
  }, []);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.full_name || !form.phone || !form.email) {
      setError('Please fill in your name, phone, and email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/funnel/franchise-apply`, { ...form, ...utmData });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY}, #2d3f8a)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '48px 36px', textAlign: 'center', maxWidth: 500, width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: NAVY, marginBottom: 10 }}>Application Received!</h2>
        <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.7, marginBottom: 28 }}>
          Thank you, {form.full_name.split(' ')[0]}! Our team will review your application and contact you shortly.
        </p>

        <div style={{ display: 'grid', gap: 12, textAlign: 'left' }}>
          {[
            { step: '1', title: 'Application Review', desc: 'Our team reviews your background and investment capacity within 24 hours.' },
            { step: '2', title: 'Regional Manager Call', desc: 'Your designated RCM will call you within 48 hours for a discovery conversation.' },
            { step: '3', title: 'Discovery Call & Agreement', desc: 'We walk you through the full model and finalize the franchise agreement together.' },
          ].map(s => (
            <div key={s.step} style={{ display: 'flex', gap: 14, padding: '14px', background: '#f8fafc', borderRadius: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                {s.step}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>{s.title}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#f8fafc' }}>
      <style>{`
        input:focus, select:focus, textarea:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.15); }
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2d3f8a 60%, #1a3a7a 100%)`, padding: '60px 24px 80px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 20, padding: '6px 16px', marginBottom: 20 }}>
            <span style={{ fontSize: 14, color: GOLD, fontWeight: 700 }}>⚡ FRANCHISE OPPORTUNITY</span>
          </div>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, color: '#fff', lineHeight: 1.2, marginBottom: 16 }}>
            Build Your <span style={{ color: GOLD }}>AI Education Business</span><br />with Nova Era Labs
          </h1>
          <p style={{ fontSize: 'clamp(14px, 2.5vw, 18px)', color: 'rgba(255,255,255,0.8)', maxWidth: 600, margin: '0 auto 36px', lineHeight: 1.7 }}>
            Join India's fastest-growing AI education franchise. Earn ₹12,600 per enrollment with full training, marketing support, and a proven business model.
          </p>

          {/* Stats Bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, maxWidth: 720, margin: '0 auto' }}>
            {[
              { value: '₹12,600', label: 'Commission / Enrollment' },
              { value: '4 Tiers', label: 'Franchise Models' },
              { value: '100%', label: 'Training & Support' },
              { value: '24hrs', label: 'Response Time' },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: '16px 14px', backdropFilter: 'blur(4px)' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: GOLD }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 3, fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2d3f8a)`, height: 40, clipPath: 'ellipse(100% 100% at 50% 0%)' }} />

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 24px 60px' }}>
        {/* Franchise Tiers */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: NAVY, margin: 0 }}>Choose Your Franchise Tier</h2>
          <p style={{ color: '#64748b', marginTop: 6, fontSize: 15 }}>Start small or go big — we have a model for every ambition</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginBottom: 60 }}>
          {TIERS.map(t => (
            <div key={t.name} style={{
              background: t.highlight ? `linear-gradient(135deg, ${NAVY}, #2d3f8a)` : '#fff',
              border: t.highlight ? `2px solid ${GOLD}` : '1.5px solid #e2e8f0',
              borderRadius: 16, padding: '22px 20px',
              boxShadow: t.highlight ? '0 8px 30px rgba(27,42,107,0.25)' : '0 2px 8px rgba(0,0,0,0.05)',
              position: 'relative',
            }}>
              {t.highlight && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: GOLD, color: '#fff', borderRadius: 10, padding: '3px 12px', fontSize: 11, fontWeight: 800 }}>
                  MOST POPULAR
                </div>
              )}
              <div style={{ fontSize: 32, marginBottom: 10 }}>{t.icon}</div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: t.highlight ? '#fff' : NAVY, margin: '0 0 4px' }}>{t.name}</h3>
              <div style={{ fontSize: 13, color: t.highlight ? GOLD : '#64748b', fontWeight: 600, marginBottom: 10 }}>{t.coverage}</div>
              <div style={{ background: t.highlight ? 'rgba(201,168,76,0.2)' : '#f8fafc', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: t.highlight ? 'rgba(255,255,255,0.6)' : '#94a3b8', fontWeight: 700 }}>INVESTMENT</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: t.highlight ? GOLD : NAVY }}>{t.investment}</div>
                <div style={{ fontSize: 11, color: t.highlight ? 'rgba(255,255,255,0.6)' : '#94a3b8', marginTop: 4, fontWeight: 700 }}>COMMISSION</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.highlight ? '#fff' : '#15803d' }}>{t.commission}</div>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {t.features.map(f => (
                  <li key={f} style={{ fontSize: 12, color: t.highlight ? 'rgba(255,255,255,0.8)' : '#475569', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: t.highlight ? GOLD : '#15803d', fontWeight: 700 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Application Form */}
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 20, padding: '36px 32px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', maxWidth: 700, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY, margin: 0 }}>Apply for Franchise</h2>
            <p style={{ color: '#64748b', marginTop: 6, fontSize: 14 }}>Free consultation · No obligation · Response within 48 hours</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: 16 }}>
              {/* Name */}
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input style={inputStyle} placeholder="Your full name" value={form.full_name}
                  onChange={e => set('full_name', e.target.value)} required />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Phone *</label>
                  <input style={inputStyle} type="tel" placeholder="9876543210" value={form.phone}
                    onChange={e => set('phone', e.target.value)} required />
                </div>
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input style={inputStyle} type="email" placeholder="you@email.com" value={form.email}
                    onChange={e => set('email', e.target.value)} required />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input style={inputStyle} placeholder="e.g. Hyderabad" value={form.city}
                    onChange={e => set('city', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>State</label>
                  <select style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                    <option value="">Select State</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Current Profession</label>
                <input style={inputStyle} placeholder="e.g. Business owner, Teacher, IT Professional" value={form.current_profession}
                  onChange={e => set('current_profession', e.target.value)} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Investment Capacity</label>
                  <select style={inputStyle} value={form.investment_capacity} onChange={e => set('investment_capacity', e.target.value)}>
                    <option value="">Select range</option>
                    <option value="Under ₹5L">Basic — Under ₹5L</option>
                    <option value="₹5L–₹10L">Standard — ₹5L–₹10L</option>
                    <option value="₹10L+">Premium — ₹10L+</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Franchise Type Interest</label>
                  <select style={inputStyle} value={form.franchise_type_interest} onChange={e => set('franchise_type_interest', e.target.value)}>
                    <option value="">Select type</option>
                    <option value="Micro Operator">Micro Operator</option>
                    <option value="Area Franchise">Area Franchise</option>
                    <option value="Regional Franchise">Regional Franchise</option>
                    <option value="Master Franchise">Master Franchise</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Brief Background</label>
                <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }}
                  placeholder="Tell us a bit about yourself and why you're interested in the franchise…"
                  value={form.brief_background} onChange={e => set('brief_background', e.target.value)} />
              </div>

              {error && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 10, fontSize: 13 }}>{error}</div>
              )}

              <button type="submit" disabled={submitting}
                style={{
                  padding: '15px', background: submitting ? '#94a3b8' : `linear-gradient(135deg, ${NAVY}, #2d3f8a)`,
                  color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                }}>
                {submitting ? 'Submitting…' : '🚀 Submit Franchise Application'}
              </button>

              <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', margin: 0 }}>
                Free consultation · No commitment required · Our team reviews within 24 hours
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: NAVY, padding: '24px', textAlign: 'center' }}>
        <div style={{ color: GOLD, fontWeight: 800, fontSize: 16, marginBottom: 4 }}>⚡ Nova Era Labs</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>India's AI Education Franchise Network</div>
      </div>
    </div>
  );
}
